import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Frontend leg of the Xero master OAuth dance. Xero redirects here with
 * ?code=…&state=… (or ?error=…). We POST {code,state} to the backend,
 * which exchanges for tokens + discovers the tenant id, then we bounce
 * back to /integrations/xero with a success/error query param.
 */
export default function XeroOAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("working");
  const [msg, setMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const code = params.get("code");
    const state = params.get("state");
    const err = params.get("error") || params.get("error_description");
    if (err) {
      navigate(`/integrations/xero?xero_error=${encodeURIComponent(err)}`,
               { replace: true });
      return;
    }
    if (!code || !state) {
      setStatus("error");
      setMsg("Xero did not return a code or state.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post(
          "/integrations/xero/oauth/callback", { code, state });
        setStatus("success");
        setTimeout(() => navigate(
          `/integrations/xero?xero_connected=1&tenant=${encodeURIComponent(data.tenant_name || "")}`,
          { replace: true }), 600);
      } catch (e) {
        // 422 from FastAPI/Pydantic returns `detail` as an array of error
        // objects ({type, loc, msg, input, url}) — render directly would
        // crash React. Coerce to a readable string in every case.
        const raw = e.response?.data?.detail ?? e.response?.data ?? e.message;
        let detail;
        if (Array.isArray(raw)) {
          detail = raw.map((r) => {
            const where = Array.isArray(r?.loc) ? r.loc.join(".") : "";
            return `${where ? where + ": " : ""}${r?.msg || JSON.stringify(r)}`;
          }).join(" · ");
        } else if (typeof raw === "object" && raw !== null) {
          detail = JSON.stringify(raw);
        } else {
          detail = String(raw || "Unknown error");
        }
        setStatus("error");
        setMsg(detail);
      }
    })();
  }, [params, navigate]);

  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="xero-oauth-callback">
      <div className="max-w-md w-full text-center space-y-3 p-8">
        {status === "working" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-[#13B5EA] animate-spin" />
            <div className="font-display font-bold text-xl">Finalising Xero connection…</div>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <div className="font-display font-bold text-xl">Connected!</div>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="h-10 w-10 mx-auto text-red-600" />
            <div className="font-display font-bold text-xl">Connection failed</div>
            <p className="text-sm text-red-700 font-mono break-words">{msg}</p>
            <button onClick={() => navigate("/integrations/xero", { replace: true })}
              className="mt-3 inline-flex items-center rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white text-sm px-4 h-9"
              data-testid="xero-oauth-back">
              Back to Xero
            </button>
          </>
        )}
      </div>
    </div>
  );
}
